<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useDialog } from 'naive-ui';
import {
  TaskStatus,
  type AnnotationFilter,
  type ContractGenerationTemplateVariantInDb,
  type ContractGenerationTemplateFieldInDb,
  type ContractGenerationTemplateFieldsResponse,
  type ContractGenerationTemplateInDb,
  type FieldCatalog,
  type TemplateAnnotation,
  TemplateFieldType,
} from '@/api';
import { TemplatesService } from '@/services/contractGeneration/templatesService';
import { TemplateVariantsService } from '@/services/contractGeneration/templateVariantsService';
import { TemplateAuthoringService } from '@/services/contractGeneration/templateAuthoringService';
import { RenderError } from '@/services/contractGeneration/renderService';
import {
  mergeFiltersInto,
  renderJinjaPreview,
  useTemplateAnnotations,
  variableNameForPreview,
  type AnnotationDraft,
} from '@/composables/contractGeneration/useTemplateAnnotations';
import {
  useTemplateFieldDraft,
  type FieldMetadataDraft,
} from '@/composables/contractGeneration/useTemplateFieldDraft';
import { useTemplateValidationPolling } from '@/composables/contractGeneration/useTemplateValidationPolling';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  buildFieldTree,
  type FieldTreeModel,
  type FieldTreeRow,
} from '@/utils/contractGeneration/templateFieldTree';
import {
  hasAuthoredProse,
  resolveFieldLabel,
} from '@/utils/contractGeneration/templateFieldLabels';
import {
  locatableIssueTarget,
  type ValidationIssueTarget,
} from '@/utils/contractGeneration/templateValidationIssues';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaSplit from '@/components/design/base/GaiaSplit.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaVariantChip from '@/components/design/domain/templating/GaiaVariantChip.vue';
import TemplatingBreadcrumb from '@/components/contractGeneration/TemplatingBreadcrumb.vue';
import AnnotatedPreview from '@/components/contractGeneration/templateAuthoring/AnnotatedPreview.vue';
import AnnotationConfigForm from '@/components/contractGeneration/templateAuthoring/AnnotationConfigForm.vue';
import FieldTree from '@/components/contractGeneration/templateFields/FieldTree.vue';
import FieldEditor from '@/components/contractGeneration/templateFields/FieldEditor.vue';
import FieldInspectorHeader from '@/components/contractGeneration/templateFields/FieldInspectorHeader.vue';
import FieldOccurrences from '@/components/contractGeneration/templateFields/FieldOccurrences.vue';
import { IconDocumentText, IconList } from '@/components/design/icons';
import { gaiaNeutral, gaiaShellPadding, gaiaStatus } from '@/components/design/themes/tokens';
import { templateVariantRoute } from '@/router/links';

/**
 * The Fields page of a template variant: two panes, and the right one follows
 * the left.
 *
 * - Fields view (the tree): a card naming the selected field, its label,
 *   description and guidance, then "Field value" — default value and options,
 *   which every tag of the field shares.
 * - Document view (the preview): the same field card with an "‹ k of N ›"
 *   pager — a field used in five places is walked one tag at a time, the
 *   document scrolling to each — then "Display at this place": that tag's
 *   formatting (each rule can be sent to all places), or a section tag's
 *   expression, and Remove.
 *
 * A tag's name, group and sub-field are not edited here: they come from the
 * Word document and change when the document does.
 *
 * One Save writes the rewritten document and the guidance together. Tags are
 * joined to fields by the `schema_path` the backend serves on each annotation
 * — the only reliable link for a chip inside a `{% for %}`.
 */

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const dialog = useDialog();

const templateUid = computed<string>(() => {
  const value = route.params.uid;
  return typeof value === 'string' ? value : '';
});

const variantUid = computed<string>(() => {
  const value = route.params.variantUid;
  return typeof value === 'string' ? value : '';
});

const template = ref<ContractGenerationTemplateInDb | null>(null);
const variant = ref<ContractGenerationTemplateVariantInDb | null>(null);
const fieldsResponse = ref<ContractGenerationTemplateFieldsResponse | null>(null);
const catalog = ref<FieldCatalog | null>(null);
const previewHtml = ref<string>('');
const loading = ref<boolean>(true);
const loadError = ref<string | null>(null);
const saving = ref<boolean>(false);

const {
  annotations,
  instances,
  isDirty: annotationsDirty,
  reset: resetAnnotations,
  update: updateAnnotation,
  remove: removeAnnotation,
  toPayload: toAnnotationPayload,
  markSaved: markAnnotationsSaved,
} = useTemplateAnnotations();

const {
  isDirty: fieldsDirty,
  dirtyPaths,
  adoptedOrphans,
  get: getDraft,
  update: updateDraft,
  reorderSiblings,
  adoptOrphan,
  reset: resetFields,
  absorb: absorbFields,
  markSaved: markFieldsSaved,
  toPayload: toFieldPayload,
} = useTemplateFieldDraft();

const isDirty = computed<boolean>(() => annotationsDirty.value || fieldsDirty.value);

// ──────────────────────────────────────────────────────────
// Loading
// ──────────────────────────────────────────────────────────

async function loadAll(): Promise<void> {
  loading.value = true;
  loadError.value = null;
  try {
    const [loadedTemplate, detail, fields, html, loadedCatalog, bundle] = await Promise.all([
      TemplatesService.getById(templateUid.value),
      TemplateVariantsService.get(templateUid.value, variantUid.value),
      TemplateAuthoringService.getFields(variantUid.value),
      TemplateAuthoringService.getPreviewHtml(variantUid.value),
      TemplateAuthoringService.getFieldCatalog(),
      TemplateAuthoringService.getAnnotations(variantUid.value),
    ]);
    template.value = loadedTemplate;
    variant.value = detail;
    fieldsResponse.value = fields;
    previewHtml.value = html;
    catalog.value = loadedCatalog;
    resetAnnotations(bundle.annotations);
    resetFields(fields);
    pendingRenames.value = new Map();
    if (!selectedPath.value) {
      const first = tree.value.rows.at(0);
      if (first) selectField(first.path);
    }
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : 'Failed to load the fields.';
  } finally {
    loading.value = false;
  }
}

/**
 * The refresh behind the validation poll: the variant (status + schema) and
 * the guidance rows, landed around whatever the author is editing. The tags
 * are not re-read — the document does not change while it validates.
 */
async function refreshSilently(): Promise<void> {
  const uid = variantUid.value;
  if (!uid) return;
  try {
    const [detail, fields] = await Promise.all([
      TemplateVariantsService.get(templateUid.value, uid),
      TemplateAuthoringService.getFields(uid),
    ]);
    if (variantUid.value !== uid) return;
    variant.value = detail;
    fieldsResponse.value = fields;
    absorbFields(fields);
  } catch (error) {
    logger.warn('Background refresh of the field editor failed:', error);
  }
}

onMounted(() => {
  void loadAll();
});

// ──────────────────────────────────────────────────────────
// Validation status
// ──────────────────────────────────────────────────────────

const templateStatus = computed<TaskStatus | null>(() => variant.value?.template_status ?? null);

const isValidating = computed<boolean>(
  () => templateStatus.value === TaskStatus.PENDING || templateStatus.value === TaskStatus.RUNNING
);

const hasValidationError = computed<boolean>(() => templateStatus.value === TaskStatus.FAILED);

useTemplateValidationPolling(isValidating, refreshSilently);

const validationErrorMessages = computed<string[]>(() =>
  (variant.value?.validation_errors ?? [])
    .map(error => error.message.trim())
    .filter(message => message.length > 0)
);

/**
 * "Template validated" shows for a few seconds once a re-validation lands, so
 * the wait has a visible end. It is armed by a save that rewrote the document
 * (the worker is often done before the first refetch, so a running → done flip
 * cannot be relied on) or by seeing the template validating, and settled by
 * the first done status after that. A failure is not transient: its banner
 * stays, with a way to each broken tag, until a save or upload fixes it.
 */
const awaitingVerdict = ref<boolean>(false);
const validationPassedVisible = ref<boolean>(false);
let passedTimer: number | null = null;
const PASSED_VISIBLE_MS = 5000;

function settleVerdict(): void {
  if (!awaitingVerdict.value) return;
  if (templateStatus.value === TaskStatus.DONE) {
    awaitingVerdict.value = false;
    if (passedTimer !== null) window.clearTimeout(passedTimer);
    validationPassedVisible.value = true;
    passedTimer = window.setTimeout(() => {
      validationPassedVisible.value = false;
      passedTimer = null;
    }, PASSED_VISIBLE_MS);
  } else if (templateStatus.value === TaskStatus.FAILED) {
    awaitingVerdict.value = false;
  }
}

watch(isValidating, validating => {
  if (validating) awaitingVerdict.value = true;
});
watch(templateStatus, settleVerdict);

onBeforeUnmount(() => {
  if (passedTimer !== null) window.clearTimeout(passedTimer);
});

// A validation message that names a tag can take the author to it: the
// document view opens with that chip selected and scrolled into view.
function normalizeTagText(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function findIssueAnnotation(target: ValidationIssueTarget): AnnotationDraft | null {
  const live = annotations.value.filter(annotation => !annotation.deleted);
  if (target.mode === 'field') {
    return (
      live.find(
        annotation =>
          annotation.schema_path === target.query ||
          variableNameForPreview(annotation) === target.query
      ) ?? null
    );
  }
  const query = normalizeTagText(target.query);
  return (
    live.find(annotation => {
      const text = normalizeTagText(
        annotation.origin_signature?.original_text ?? renderJinjaPreview(annotation)
      );
      return text === query || text.startsWith(query);
    }) ?? null
  );
}

function showIssueInDocument(messageText: string): void {
  const target = locatableIssueTarget(messageText);
  if (!target) return;
  const annotation = findIssueAnnotation(target);
  if (!annotation) {
    message.warning('Couldn’t find that tag in the document — it may have changed since.');
    return;
  }
  leftView.value = 'document';
  handleClickTag(annotation);
}

// ──────────────────────────────────────────────────────────
// Left pane: the field list or the document
// ──────────────────────────────────────────────────────────

type LeftView = 'fields' | 'document';

const leftView = ref<LeftView>('fields');

/**
 * The workspace switch lives in the page header, beside Save: it changes what
 * the whole page is about (the right pane follows), not just the left list.
 * Icons because the two views are told apart by shape as much as by word.
 */
const leftViewOptions: GaiaSegmentedOption[] = [
  { label: 'Fields', value: 'fields', icon: IconList },
  { label: 'Document', value: 'document', icon: IconDocumentText },
];

/** The document wants more room than the list; the split follows the switch. */
const splitSize = ref<number>(0.36);

watch(leftView, view => {
  splitSize.value = view === 'document' ? 0.56 : 0.36;
});

// ──────────────────────────────────────────────────────────
// The tree, the tags, and the selection
// ──────────────────────────────────────────────────────────

const tree = computed<FieldTreeModel>(() =>
  buildFieldTree(
    variant.value?.extracted_schema?.fields ?? null,
    fieldsResponse.value,
    path => getDraft(path).order_index
  )
);

const livePaths = computed<Set<string>>(() => new Set(tree.value.rows.map(row => row.path)));

/** Every live tag of a field, in document order — the join the backend serves. */
const occurrencesByPath = computed<Map<string, AnnotationDraft[]>>(() => {
  const map = new Map<string, AnnotationDraft[]>();
  for (const annotation of annotations.value) {
    if (annotation.deleted || annotation.kind !== 'inline' || !annotation.schema_path) continue;
    const list = map.get(annotation.schema_path) ?? [];
    list.push(annotation);
    map.set(annotation.schema_path, list);
  }
  return map;
});

const tagCount = computed<number>(
  () => annotations.value.filter(annotation => !annotation.deleted).length
);

const selectedPath = ref<string | null>(null);
/**
 * The tag being edited: one of the selected field's occurrences, or — with
 * no field selected — a tag that is not a field (a section opener), which
 * only has settings.
 */
const selectedLocalId = ref<string | null>(null);

const selectedRow = computed<FieldTreeRow | null>(() =>
  selectedPath.value ? (tree.value.byPath.get(selectedPath.value) ?? null) : null
);

const selectedOrphan = computed<ContractGenerationTemplateFieldInDb | null>(
  () => tree.value.orphans.find(orphan => orphan.path === selectedPath.value) ?? null
);

const selectedDraft = computed<FieldMetadataDraft | null>(() =>
  selectedRow.value ? getDraft(selectedRow.value.path) : null
);

const selectedOccurrences = computed<AnnotationDraft[]>(() =>
  selectedRow.value ? (occurrencesByPath.value.get(selectedRow.value.path) ?? []) : []
);

/**
 * Filters every tag of this field already carries — what "All N places" means,
 * read off the document rather than remembered from a previous session.
 *
 * Broadcasting is a one-shot merge at apply time and leaves no flag behind, so
 * this is the only honest source: a filter is on all places when it is on all
 * places. Without it the control reset to "Only here" each time the form
 * opened, reporting the opposite of what had just been done.
 */
const sharedFilterNames = computed<string[]>(() => {
  const occurrences = selectedOccurrences.value;
  if (occurrences.length < 2) return [];
  const [first, ...rest] = occurrences;
  return (first.filters ?? [])
    .map(filter => filter.name)
    .filter(name => rest.every(other => (other.filters ?? []).some(f => f.name === name)));
});

const selectedOccurrence = computed<AnnotationDraft | null>(
  () =>
    selectedOccurrences.value.find(occurrence => occurrence.local_id === selectedLocalId.value) ??
    selectedOccurrences.value.at(0) ??
    null
);

/** A selected tag that is not a field — settings only, no guidance. */
const selectedStandalone = computed<AnnotationDraft | null>(() => {
  if (selectedPath.value || !selectedLocalId.value) return null;
  return (
    annotations.value.find(annotation => annotation.local_id === selectedLocalId.value) ?? null
  );
});

/** The tag under the pointer: a section tag on its own, or the chosen occurrence. */
const configuredAnnotation = computed<AnnotationDraft | null>(
  () => selectedStandalone.value ?? selectedOccurrence.value
);

type RightMode = 'empty' | 'orphan' | 'field' | 'occurrence' | 'standalone';

/** What the right pane is for — it follows which left view is open. */
const rightMode = computed<RightMode>(() => {
  if (selectedStandalone.value) return 'standalone';
  if (selectedOrphan.value) return 'orphan';
  if (!selectedRow.value) return 'empty';
  return leftView.value === 'document' ? 'occurrence' : 'field';
});

/**
 * The tag the settings form is bound to: in the Fields view any one of the
 * field's tags stands in for the field (only its shared settings are edited
 * there), in the Document view the one that was clicked or stepped to.
 */
const formAnnotation = computed<AnnotationDraft | null>(() => configuredAnnotation.value);

/** A section tag has no field to send the author to, so it keeps every setting. */
const formScope = computed<'all' | 'field' | 'occurrence'>(() => {
  if (rightMode.value === 'field') return 'field';
  if (rightMode.value === 'occurrence') return 'occurrence';
  return 'all';
});

/**
 * The type the settings form adapts its controls to. The extracted schema is
 * the authority — it knows `employee.ZIP_code | format_date` is a date from
 * the field as a whole — so it wins whenever the selected row has one. A tag
 * without a row, or a schema still being re-extracted, falls back to what the
 * tag itself says through its binding, options and filters.
 */
const configuredFieldType = computed<TemplateFieldType | null>(() => {
  const schemaType = selectedStandalone.value ? undefined : selectedRow.value?.schema?.type;
  if (schemaType && schemaType !== 'unknown') return schemaType;
  return formAnnotation.value ? deriveFieldTypeForAnnotation(formAnnotation.value) : null;
});

/**
 * Tags of the selected field removed in this session. The tree still lists the
 * field (it comes from the saved schema), so say what happened instead of the
 * "groups the ones under it" note.
 */
const removedNote = computed<string | null>(() => {
  const path = selectedRow.value?.path;
  if (!path) return null;
  const removed = annotations.value.filter(
    annotation =>
      annotation.deleted && annotation.kind === 'inline' && annotation.schema_path === path
  ).length;
  if (removed === 0) return null;
  if (removed === 1) return 'The only tag of this field was removed — save to drop the field.';
  return `All ${removed} tags of this field were removed — save to drop the field.`;
});

/** From the Document view's field summary: back to the tree, same field selected. */
function openFieldFromDocument(): void {
  leftView.value = 'fields';
}

/** How often the selected field is written into the document, in the author's words. */
const occurrenceSummary = computed<string>(() => {
  const count = selectedOccurrences.value.length;
  if (count === 0) return 'Not written directly in the document';
  if (count === 1) return 'Appears once in the document';
  return `Appears ${count} times in the document`;
});

/** Chips to light up in the document: all of the field's, the chosen one first. */
const activeLocalIds = computed<string[]>(() => {
  if (selectedStandalone.value) return [selectedStandalone.value.local_id];
  const ids = selectedOccurrences.value.map(occurrence => occurrence.local_id);
  const chosen = selectedOccurrence.value?.local_id;
  if (chosen) return [chosen, ...ids.filter(id => id !== chosen)];
  return ids;
});

function selectField(path: string, localId: string | null = null): void {
  selectedPath.value = path;
  selectedLocalId.value = localId ?? occurrencesByPath.value.get(path)?.at(0)?.local_id ?? null;
}

// A refresh can take the selected path away; don't point at nothing.
watch(tree, model => {
  const path = selectedPath.value;
  if (!path) return;
  if (!model.byPath.has(path) && !model.orphans.some(orphan => orphan.path === path)) {
    selectedPath.value = null;
    selectedLocalId.value = null;
  }
});

function resolveLabel(row: FieldTreeRow): string {
  return resolveFieldLabel(getDraft(row.path), row.fallbackLabel);
}

function hasNotes(path: string): boolean {
  return hasAuthoredProse(getDraft(path));
}

function isDirtyPath(path: string): boolean {
  return dirtyPaths.value.has(path);
}

const notesCount = computed<number>(() => tree.value.rows.filter(row => hasNotes(row.path)).length);

const selectedAdoptedTo = computed<string | null>(() => {
  const orphan = selectedOrphan.value;
  if (!orphan) return null;
  const targetPath = adoptedOrphans.value.get(orphan.path);
  if (!targetPath) return null;
  const target = tree.value.byPath.get(targetPath);
  return target ? resolveLabel(target) : targetPath;
});

function handleGuidanceUpdate(patch: Partial<FieldMetadataDraft>): void {
  if (selectedRow.value) updateDraft(selectedRow.value.path, patch);
}

function handleAdopt(targetPath: string): void {
  if (selectedOrphan.value) adoptOrphan(selectedOrphan.value, targetPath);
}

/**
 * A tag in the document was clicked. A field's tag selects the field with
 * that occurrence under Settings; a tag that is not a field (a section
 * opener or closer) is shown on its own, settings only.
 */
function handleClickTag(annotation: AnnotationDraft): void {
  if (annotation.schema_path && tree.value.byPath.has(annotation.schema_path)) {
    selectField(annotation.schema_path, annotation.local_id);
    return;
  }
  selectedPath.value = null;
  selectedLocalId.value = annotation.local_id;
}

// ──────────────────────────────────────────────────────────
// Settings — the tag's configuration
// ──────────────────────────────────────────────────────────

function deriveFieldTypeForAnnotation(annotation: AnnotationDraft): TemplateFieldType | null {
  const binding = annotation.binding;
  if (binding?.kind === 'entity') {
    const entity = catalog.value?.entities.find(e => e.name === binding.entity_type);
    const subField = entity?.fields.find(f => f.name === binding.sub_field);
    return subField?.type ?? null;
  }
  // Selection-typed primitives carry their options list — that's the clearest
  // type signal, so prefer it. Otherwise fall back to filter intersection.
  if (annotation.selection_options && annotation.selection_options.length > 0) {
    return TemplateFieldType.SELECTION;
  }
  return inferTypeFromFilters(annotation.filters ?? []);
}

/**
 * Any filters already on a primitive narrow its type: intersect each filter's
 * `applicable_types` across the catalog and take the survivor when there is
 * exactly one. Empty or ambiguous → null, and the form lists every filter;
 * backend validation stays the safety net.
 */
function inferTypeFromFilters(applied: AnnotationFilter[]): TemplateFieldType | null {
  if (applied.length === 0) return null;
  const filterDefs = catalog.value?.filters ?? [];
  const perFilterTypes = applied
    .map(f => filterDefs.find(def => def.name === f.name)?.applicable_types)
    .filter((arr): arr is TemplateFieldType[] => Array.isArray(arr) && arr.length > 0);
  if (perFilterTypes.length === 0) return null;
  const intersection = perFilterTypes.reduce<TemplateFieldType[]>(
    (acc, types) => acc.filter(t => types.includes(t)),
    [...perFilterTypes[0]]
  );
  return intersection.length === 1 ? intersection[0] : null;
}

/** Settings that belong to the field, so every tag producing it must agree. */
const FIELD_LEVEL_KEYS = ['binding', 'field_name', 'default_value', 'selection_options'] as const;

type Binding = NonNullable<TemplateAnnotation['binding']>;

interface AliasRename {
  kind: 'entity' | 'object';
  entityType: string | null;
  from: string;
  to: string;
}

/** The group rename a binding change amounts to, if it is one. */
function aliasRenameOf(
  before: TemplateAnnotation['binding'],
  after: TemplateAnnotation['binding']
): AliasRename | null {
  if (!before || !after) return null;
  if (before.kind === 'entity' && after.kind === 'entity') {
    if (before.entity_type !== after.entity_type) return null;
    if (before.instance_alias === after.instance_alias) return null;
    return {
      kind: 'entity',
      entityType: before.entity_type,
      from: before.instance_alias,
      to: after.instance_alias,
    };
  }
  if (before.kind === 'object' && after.kind === 'object') {
    if (before.instance_alias === after.instance_alias) return null;
    return {
      kind: 'object',
      entityType: null,
      from: before.instance_alias,
      to: after.instance_alias,
    };
  }
  return null;
}

/** The same binding with the group renamed, or null when it isn't that group. */
function withRenamedAlias(binding: Binding, rename: AliasRename): Binding | null {
  if (binding.kind === 'entity') {
    if (rename.kind !== 'entity') return null;
    if (binding.entity_type !== rename.entityType || binding.instance_alias !== rename.from) {
      return null;
    }
    return { ...binding, instance_alias: rename.to };
  }
  if (binding.kind === 'object') {
    if (rename.kind !== 'object' || binding.instance_alias !== rename.from) return null;
    return { ...binding, instance_alias: rename.to };
  }
  return null;
}

/**
 * Apply the form's changes. Default and options are the field's: they go to
 * every tag of the same field, and only the Fields view may change them.
 * Output filters (and a section tag's expression) are the one tag's, changed
 * from the Document view — unless the author asks for the filters to reach
 * every tag of the field. Identity is handled the same way as the field's for
 * the drawer's sake — renaming a group renames it on every tag that carries
 * it, and every path that changes is remembered so guidance written in this
 * session is saved under the new path — but this page's forms never send it.
 */
function handleApplyConfig(
  patch: Partial<TemplateAnnotation>,
  options?: { broadcastFilters?: AnnotationFilter[] }
): void {
  const target = formAnnotation.value;
  if (!target) return;
  // The field's: default and options, set from the field list (identity is
  // listed too for the drawer's sake, but this page never edits it — names
  // come from the Word document). They reach every tag of the field.
  const fieldLevel: Partial<TemplateAnnotation> = {};
  for (const key of FIELD_LEVEL_KEYS) {
    if (key in patch) Object.assign(fieldLevel, { [key]: patch[key] });
  }
  // Filters ticked "all N tags" on their card: merged into every other tag of
  // the field on top of what that tag already has (see `mergeFiltersInto`).
  const broadcast = options?.broadcastFilters ?? [];
  const replacedOnSiblings: string[] = [];
  // A selection renders through `choices(...)`; other filters are
  // unsupported on it, so becoming one clears them on every tag.
  if (fieldLevel.selection_options && fieldLevel.selection_options.length > 0) {
    fieldLevel.filters = [];
  }
  const hasFieldLevel = Object.keys(fieldLevel).length > 0;

  // What belongs to this one tag: whatever the patch carries that is not the field's.
  const occurrenceLevel: Partial<TemplateAnnotation> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (key in fieldLevel) continue;
    Object.assign(occurrenceLevel, { [key]: value });
  }

  const siblings = target.schema_path
    ? (occurrencesByPath.value.get(target.schema_path) ?? [target])
    : [target];
  for (const sibling of siblings) {
    const isTarget = sibling.local_id === target.local_id;
    if (isTarget) {
      updateAnnotation(sibling.local_id, { ...fieldLevel, ...occurrenceLevel });
    } else {
      const siblingPatch: Partial<TemplateAnnotation> = { ...fieldLevel };
      if (broadcast.length > 0) {
        const merged = mergeFiltersInto(
          sibling.filters ?? [],
          broadcast,
          catalog.value?.filters ?? []
        );
        siblingPatch.filters = merged.filters;
        replacedOnSiblings.push(...merged.replaced);
      }
      if (Object.keys(siblingPatch).length === 0) continue;
      updateAnnotation(sibling.local_id, siblingPatch);
    }
    if (hasFieldLevel) recordRename(sibling, { ...sibling, ...fieldLevel });
  }

  if (!hasFieldLevel) {
    if (broadcast.length === 0) {
      message.success('Applied to this tag in the document preview — save to keep it.');
      return;
    }
    const names = broadcast.map(filter => filter.name).join(', ');
    const others = siblings.length - 1;
    const replaced = [...new Set(replacedOnSiblings)];
    const replacedNote =
      replaced.length > 0 ? `, replacing ${replaced.join(', ')} where it was set` : '';
    message.success(
      `Applied to this tag; ${names} also put on the other ${others} ${others === 1 ? 'tag' : 'tags'} of this field${replacedNote} — save to keep it.`
    );
    return;
  }

  const after: AnnotationDraft = { ...target, ...fieldLevel };
  const rename = aliasRenameOf(target.binding, after.binding);
  if (rename) {
    const siblingIds = new Set(siblings.map(sibling => sibling.local_id));
    for (const annotation of annotations.value) {
      if (siblingIds.has(annotation.local_id) || annotation.deleted || !annotation.binding)
        continue;
      const renamed = withRenamedAlias(annotation.binding, rename);
      if (!renamed) continue;
      updateAnnotation(annotation.local_id, { binding: renamed });
      recordRename(annotation, { ...annotation, binding: renamed });
    }
  }
  message.success(
    siblings.length > 1
      ? `Applied to all ${siblings.length} places in the document preview — save to keep it.`
      : 'Applied to the document preview — save to keep it.'
  );
}

/** Remove the tag under Tag settings from the document (the field keeps its other tags). */
function handleRemoveConfigured(): void {
  const target = formAnnotation.value;
  if (!target) return;
  removeAnnotation(target.local_id);
  if (selectedStandalone.value) {
    selectedLocalId.value = null;
    return;
  }
  selectedLocalId.value =
    selectedOccurrences.value.find(occurrence => occurrence.local_id !== target.local_id)
      ?.local_id ?? null;
}

// ──────────────────────────────────────────────────────────
// Renames — guidance follows the tag
// ──────────────────────────────────────────────────────────

/** Old path → the path the field will have after the save, for this session's renames. */
const pendingRenames = ref<Map<string, string>>(new Map());

/**
 * Where a renamed tag's field will live after the save. Outside a loop the
 * variable is the path; inside one the loop's prefix stays and only the tail
 * the tag names changes. Null when it cannot be told (the loop variable
 * itself was renamed) — nothing is re-keyed then, and the server keeps the
 * old row.
 */
function predictSchemaPath(before: AnnotationDraft, after: TemplateAnnotation): string | null {
  const oldPath = before.schema_path ?? null;
  if (!oldPath) return null;
  const oldVariable = variableNameForPreview(before);
  const newVariable = variableNameForPreview(after);
  if (oldVariable === newVariable) return oldPath;
  if (oldPath === oldVariable) return newVariable;
  const [oldRoot, ...oldTail] = oldVariable.split('.');
  const [newRoot, ...newTail] = newVariable.split('.');
  if (oldRoot !== newRoot || oldTail.length === 0 || newTail.length === 0) return null;
  const tail = oldTail.join('.');
  if (!oldPath.endsWith(tail)) return null;
  return oldPath.slice(0, oldPath.length - tail.length) + newTail.join('.');
}

function recordRename(before: AnnotationDraft, after: TemplateAnnotation): void {
  const from = before.schema_path ?? null;
  const to = predictSchemaPath(before, after);
  if (!from || !to || from === to) return;
  const next = new Map(pendingRenames.value);
  // A field renamed twice in one session chains: the row is still keyed by
  // the path it had when the page loaded.
  const origin = [...next.entries()].find(([, current]) => current === from)?.[0] ?? from;
  next.set(origin, to);
  pendingRenames.value = next;
}

// ──────────────────────────────────────────────────────────
// Saving
// ──────────────────────────────────────────────────────────

const saveBlockedReason = computed<string | null>(() => {
  if (hasValidationError.value && !annotationsDirty.value) {
    return 'Validation failed — fix the tags here or upload a fixed .docx on the variant page.';
  }
  if (isValidating.value && !annotationsDirty.value) {
    return 'The template is re-validating — guidance can be saved when it finishes.';
  }
  if (!isDirty.value) return 'No changes to save.';
  return null;
});

const canSave = computed<boolean>(() => saveBlockedReason.value === null && !saving.value);

function reportWriteError(error: unknown, fallback: string): void {
  if (error instanceof RenderError && error.kind === 'still_validating') {
    message.warning('The template is re-validating — try again in a moment.');
    return;
  }
  if (error instanceof RenderError && error.kind === 'validation_failed') {
    message.error(error.messages.join('; '));
    void refreshSilently();
    return;
  }
  message.error(extractErrorDetail(error, fallback));
}

/**
 * One request: the tag list and the guidance, the latter keyed by the paths
 * the fields will have once the document is rewritten. The response is the
 * re-discovered tag list; the guidance rows are re-read since renamed rows
 * moved server-side.
 */
async function save(): Promise<void> {
  if (!canSave.value) return;
  const renames = new Map(pendingRenames.value);
  const allowed = new Set(livePaths.value);
  for (const target of renames.values()) allowed.add(target);
  const { fields, droppedPaths } = toFieldPayload(allowed, renames);
  if (droppedPaths.length > 0) {
    const count = droppedPaths.length;
    message.warning(
      `${count} edited field${count === 1 ? '' : 's'} no longer exist${count === 1 ? 's' : ''} in the document and ${count === 1 ? 'was' : 'were'} not saved.`
    );
  }
  const structureChanged = annotationsDirty.value;
  saving.value = true;
  try {
    const bundle = await TemplateAuthoringService.saveAnnotations(
      variantUid.value,
      toAnnotationPayload(),
      fields
    );
    markAnnotationsSaved(bundle.annotations);
    pendingRenames.value = new Map();
    const movedTo = selectedPath.value ? renames.get(selectedPath.value) : undefined;
    if (movedTo) selectedPath.value = movedTo;

    const [detail, refreshedFields] = await Promise.all([
      TemplateVariantsService.get(templateUid.value, variantUid.value),
      TemplateAuthoringService.getFields(variantUid.value),
    ]);
    variant.value = detail;
    fieldsResponse.value = refreshedFields;
    markFieldsSaved(refreshedFields);
    // Re-discovery minted new ids; point at the field's first tag again.
    selectedLocalId.value = selectedOccurrences.value.at(0)?.local_id ?? null;

    if (structureChanged) {
      // The document was rewritten, so a verdict is coming — or already here.
      awaitingVerdict.value = true;
      settleVerdict();
      try {
        previewHtml.value = await TemplateAuthoringService.getPreviewHtml(variantUid.value);
      } catch (error) {
        logger.warn('Preview refresh failed after save', error);
      }
      message.success('Saved — the template is re-validating in the background.');
    } else {
      message.success('Saved.');
    }
  } catch (error) {
    reportWriteError(error, 'Failed to save the fields.');
  } finally {
    saving.value = false;
  }
}

async function deleteSelectedOrphan(): Promise<void> {
  const orphan = selectedOrphan.value;
  if (!orphan || saving.value) return;
  saving.value = true;
  try {
    const refreshed = await TemplateAuthoringService.deleteField(variantUid.value, orphan.path);
    fieldsResponse.value = refreshed;
    absorbFields(refreshed);
    selectedPath.value = null;
    selectedLocalId.value = null;
    message.success('Entry removed.');
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to remove the field entry.'));
  } finally {
    saving.value = false;
  }
}

// ──────────────────────────────────────────────────────────
// Leaving
// ──────────────────────────────────────────────────────────

function openVariant(): void {
  void router.push(templateVariantRoute(templateUid.value, variantUid.value));
}

// One guard for every way out — crumbs, sidebar, browser back.
onBeforeRouteLeave(async () => {
  if (!isDirty.value) return true;
  return await new Promise<boolean>(resolve => {
    dialog.warning({
      title: 'Discard unsaved changes?',
      content: 'Tags and guidance you changed here will be lost.',
      positiveText: 'Discard',
      negativeText: 'Stay',
      onPositiveClick: () => {
        resolve(true);
      },
      onNegativeClick: () => {
        resolve(false);
      },
      onClose: () => {
        resolve(false);
      },
      onMaskClick: () => {
        resolve(false);
      },
    });
  });
});

// --- chrome ------------------------------------------------------------------
const canvasSurface = gaiaNeutral.surfaceCanvas;
const surface = gaiaNeutral.surface;
const divider = gaiaNeutral.borderDivider;
const pageX = gaiaShellPadding.pageX;
const warningColor = gaiaStatus.warning;
</script>

<template>
  <Layout fill position="absolute" content-padding="0" content-max-width="100%">
    <template #breadcrumb>
      <templating-breadcrumb
        :template-uid="templateUid"
        :template-name="template?.name"
        :variant-uid="variantUid"
        :variant-name="variant?.name"
        page="Fields"
      />
    </template>

    <div class="fields" data-tour="tpl.editor">
      <header class="fields__header">
        <n-flex vertical :size="2" :style="{ minWidth: 0, flex: '1 1 auto' }">
          <n-flex align="center" :size="8" :wrap="false" :style="{ minWidth: 0 }">
            <gaia-text truncate size="subtitle" weight="semibold" tone="ink">
              {{ template ? template.name : 'Fields' }}
            </gaia-text>
            <gaia-variant-chip v-if="variant" :name="variant.name" />
          </n-flex>
          <n-flex v-if="!loading && !loadError" align="center" :size="6" :wrap="false">
            <gaia-text size="meta" tone="subtle">
              {{ tree.rows.length }} field{{ tree.rows.length === 1 ? '' : 's' }} ·
              {{ notesCount }} with guidance · {{ tagCount }} tag{{ tagCount === 1 ? '' : 's' }}
            </gaia-text>
            <n-flex v-if="isDirty" align="center" :size="5" :wrap="false">
              <gaia-status-indicator status="warning" glyph="dot" />
              <gaia-text size="meta" weight="semibold" :style="{ color: warningColor }">
                unsaved changes
              </gaia-text>
            </n-flex>
          </n-flex>
        </n-flex>

        <gaia-segmented
          v-if="!loading && !loadError"
          v-model:value="leftView"
          :options="leftViewOptions"
          size="medium"
        />

        <gaia-tooltip :disabled="canSave">
          <template #trigger>
            <span :style="{ display: 'inline-flex' }">
              <gaia-button
                type="primary"
                size="medium"
                :disabled="!canSave"
                :loading="saving"
                @click="save"
              >
                Save
              </gaia-button>
            </span>
          </template>
          {{ saveBlockedReason ?? 'Saving…' }}
        </gaia-tooltip>
      </header>

      <gaia-banner v-if="loadError" type="error" :style="{ margin: '16px 24px' }">
        {{ loadError }}
      </gaia-banner>

      <n-flex v-else-if="loading" vertical align="center" :size="12" :style="{ padding: '64px 0' }">
        <gaia-spinner size="medium" />
        <gaia-text size="row" tone="tertiary">Loading fields…</gaia-text>
      </n-flex>

      <template v-else>
        <!-- Validation as a story with an end: "re-validating" while the worker
             runs, then its verdict for a few seconds, then out of the way.
             Each banner sits in its own div: the banner's root is an n-alert,
             which carries a transition of its own, and two transitions on one
             element fight over its hooks — the outer one never learns the
             leave finished and, in out-in mode, never shows the next banner. -->
        <transition name="fields-fade" mode="out-in">
          <div v-if="isValidating" key="validating" class="fields__notice">
            <gaia-banner type="info" title="Re-validating the template">
              You can keep editing. The field list refreshes on its own when validation finishes.
            </gaia-banner>
          </div>
          <div v-else-if="validationPassedVisible" key="passed" class="fields__notice">
            <gaia-banner type="success" title="Template validated">
              Every tag was processed and the field list is up to date.
            </gaia-banner>
          </div>
          <!-- Stays until fixed. Each issue that names a tag can open it in the document. -->
          <div v-else-if="hasValidationError" key="failed" class="fields__notice">
            <gaia-banner type="error" title="Template validation failed">
              <n-flex vertical :size="6">
                <n-flex
                  v-for="(issue, index) in validationErrorMessages"
                  :key="index"
                  align="center"
                  justify="space-between"
                  :size="8"
                  :wrap="false"
                >
                  <span :style="{ minWidth: 0 }">{{ issue }}</span>
                  <gaia-button
                    v-if="locatableIssueTarget(issue)"
                    size="tiny"
                    quaternary
                    :style="{ flexShrink: 0 }"
                    @click="showIssueInDocument(issue)"
                  >
                    Show in document
                  </gaia-button>
                </n-flex>
                <span>
                  Fix the tag here and save again, or upload a fixed .docx on the variant page.
                </span>
              </n-flex>
              <template #action>
                <gaia-button size="small" @click="openVariant">Open variant</gaia-button>
              </template>
            </gaia-banner>
          </div>
        </transition>

        <div class="fields__body">
          <gaia-split v-model:size="splitSize" :min="0.24" :max="0.7" :style="{ height: '100%' }">
            <template #1>
              <div class="fields__pane">
                <field-tree
                  v-if="leftView === 'fields'"
                  :model="tree"
                  :selected-path="selectedPath"
                  :resolve-label="resolveLabel"
                  :has-notes="hasNotes"
                  :is-dirty-path="isDirtyPath"
                  :can-reorder="tree.schemaAvailable && !saving"
                  :adopted-orphans="adoptedOrphans"
                  @select="path => selectField(path)"
                  @reorder="reorderSiblings"
                />
                <div v-else class="fields__document">
                  <annotated-preview
                    :html="previewHtml"
                    :annotations="annotations"
                    :active-local-ids="activeLocalIds"
                    @click-tag="handleClickTag"
                  />
                </div>
              </div>
            </template>
            <template #2>
              <div class="fields__pane">
                <!-- Fields view: the field's facts and guidance, then what
                     every tag of it shares (default value, options). -->
                <template v-if="rightMode !== 'occurrence' && rightMode !== 'standalone'">
                  <field-inspector-header
                    v-if="rightMode === 'field' && selectedRow"
                    :title="resolveLabel(selectedRow)"
                    :path="selectedRow.path"
                    :schema="selectedRow.schema"
                  >
                    <template #actions>
                      <gaia-button size="small" quaternary @click="leftView = 'document'">
                        <template #icon>
                          <n-icon><document-text-outline /></n-icon>
                        </template>
                        View in document
                      </gaia-button>
                    </template>
                    <template #footer>{{ occurrenceSummary }}</template>
                  </field-inspector-header>
                  <field-editor
                    :row="selectedRow"
                    :orphan="selectedOrphan"
                    :draft="selectedDraft"
                    :adopt-targets="tree.rows"
                    :resolve-label="resolveLabel"
                    :has-notes="hasNotes"
                    :adopted-to="selectedAdoptedTo"
                    :disabled="saving"
                    @update="handleGuidanceUpdate"
                    @adopt="handleAdopt"
                    @delete="deleteSelectedOrphan"
                  />
                  <div v-if="rightMode === 'field' && formAnnotation" class="fields__settings">
                    <annotation-config-form
                      embedded
                      scope="field"
                      :annotation="formAnnotation"
                      :filter-catalog="catalog?.filters ?? []"
                      :entity-catalog="catalog?.entities ?? []"
                      :field-type="configuredFieldType"
                      :instances="instances"
                      @apply="handleApplyConfig"
                    />
                  </div>
                  <gaia-text
                    v-else-if="rightMode === 'field'"
                    size="meta"
                    tone="muted"
                    :style="{ display: 'block', padding: '0 22px 28px' }"
                  >
                    {{
                      removedNote ??
                      (selectedRow?.hasChildren
                        ? 'No tag of its own in the document — this field groups the ones under it.'
                        : 'This field is only used to decide what the document shows (in a condition), so it is not written into it and has no default value or formatting. The person filling in the template answers it.')
                    }}
                  </gaia-text>
                </template>

                <!-- Document view: which field this tag is, then this tag's own settings. -->
                <template v-else-if="rightMode === 'occurrence' && selectedRow">
                  <!-- The field this tag belongs to, with a way back to its
                       guidance. A field used in several places gets the pager:
                       the arrows walk through its tags, the document scrolls to
                       each, and the formatting below is that tag's own. -->
                  <field-inspector-header
                    :title="resolveLabel(selectedRow)"
                    :path="selectedRow.path"
                    :schema="selectedRow.schema"
                  >
                    <template #actions>
                      <gaia-button size="small" quaternary @click="openFieldFromDocument">
                        <template #icon>
                          <n-icon><create-outline /></n-icon>
                        </template>
                        Edit label &amp; guidance
                      </gaia-button>
                    </template>
                    <template #footer>{{ occurrenceSummary }}</template>
                    <template v-if="formAnnotation && selectedOccurrences.length > 1" #aside>
                      <field-occurrences
                        :occurrences="selectedOccurrences"
                        :active-local-id="formAnnotation.local_id"
                        :disabled="saving"
                        @select="id => (selectedLocalId = id)"
                      />
                    </template>
                  </field-inspector-header>
                  <div v-if="formAnnotation" class="fields__settings fields__settings--flush">
                    <annotation-config-form
                      embedded
                      scope="occurrence"
                      :annotation="formAnnotation"
                      :sibling-count="selectedOccurrences.length"
                      :shared-filter-names="sharedFilterNames"
                      :filter-catalog="catalog?.filters ?? []"
                      :entity-catalog="catalog?.entities ?? []"
                      :field-type="configuredFieldType"
                      :instances="instances"
                      @apply="handleApplyConfig"
                      @remove="handleRemoveConfigured"
                    />
                  </div>
                  <gaia-text
                    v-else
                    size="meta"
                    tone="muted"
                    :style="{ display: 'block', padding: '16px 22px 28px' }"
                  >
                    {{ removedNote ?? 'Click one of this field’s tags in the document.' }}
                  </gaia-text>
                </template>

                <!-- A tag that is not a field: its settings only, as before. -->
                <div v-else-if="formAnnotation" class="fields__settings">
                  <annotation-config-form
                    embedded
                    :scope="formScope"
                    :annotation="formAnnotation"
                    :filter-catalog="catalog?.filters ?? []"
                    :entity-catalog="catalog?.entities ?? []"
                    :field-type="configuredFieldType"
                    :instances="instances"
                    @apply="handleApplyConfig"
                    @remove="handleRemoveConfigured"
                  />
                </div>
              </div>
            </template>
          </gaia-split>
        </div>
      </template>
    </div>
  </Layout>
</template>

<style scoped>
/* The fill chain: a column that takes the layout's height, a pinned header,
   a card holding the split whose panes scroll inside themselves. Naive has no
   toolbar surface, so the header strip is the same CSS the variant pages carry. */
.fields {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: v-bind(canvasSurface);
}

.fields__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px v-bind(pageX);
  background: v-bind(surface);
  border-bottom: 1px solid v-bind(divider);
  flex-shrink: 0;
}

.fields__body {
  flex: 1 1 auto;
  min-height: 0;
  margin: 12px 24px 16px;
  border: 1px solid v-bind(divider);
  border-radius: 12px;
  background: v-bind(surface);
  overflow: hidden;
}

.fields__pane {
  height: 100%;
  overflow: auto;
}

.fields__document {
  padding: 12px;
}

/* The settings block continues the editor's column under a hairline. */
.fields__settings {
  padding: 16px 22px 28px;
  border-top: 1px solid v-bind(divider);
}

/* Directly under the inspector header, which draws the hairline itself. */
.fields__settings--flush {
  border-top: none;
}

.fields__notice {
  padding: 12px 24px 0;
}

/* The validation banners fade rather than pop: they are status, not alerts. */
.fields-fade-enter-active,
.fields-fade-leave-active {
  transition: opacity 0.5s ease;
}

.fields-fade-enter-from,
.fields-fade-leave-to {
  opacity: 0;
}
</style>
